import { z } from 'zod';

import type { TFunction } from 'i18next';

import {
  customFieldValuesSchema,
  refineClientOrGuest,
  refineRequiredCustomFields,
  requiredSelect,
} from './common';

const isPositiveNumber = (value: string) => Number(value) > 0;

/**
 * An event may run over several days, so the end is a date of its own rather than a duration. The
 * service and the capacity belong to the event only while it is not split into waves.
 */
type TimeRangeValues = {
  start_date: string;
  start_minute: string | null;
  end_date: string;
  end_minute: string | null;
};

// `*_minute` holds minutes from midnight, so comparing the halves as one string puts
// "1410" (23:30) before "540" (09:00) — the day compares as text, the time as a number.
const isAfterStart = (values: TimeRangeValues) => {
  if (values.end_date !== values.start_date) {
    return values.end_date > values.start_date;
  }

  return Number(values.end_minute) > Number(values.start_minute);
};

export const createBookingCompanyEventSchema = (t: TFunction) =>
  z
    .object({
      name: z.string().min(1, { message: t('validation_required') }),
      description: z.string(),
      location_id: requiredSelect(t),
      start_date: z.string().min(1, { message: t('validation_required') }),
      start_minute: requiredSelect(t),
      end_date: z.string().min(1, { message: t('validation_required') }),
      end_minute: requiredSelect(t),
      split_into_waves: z.boolean(),
      service_id: z.string().nullable(),
      capacity: z.string(),
      online_bookable: z.boolean(),
      image: z.union([z.instanceof(File), z.string()]).nullable(),
    })
    .superRefine((values, ctx) => {
      if (!values.split_into_waves) {
        if (!values.service_id) {
          ctx.addIssue({
            code: 'custom',
            path: ['service_id'],
            message: t('validation_required'),
          });
        }
        if (!isPositiveNumber(values.capacity)) {
          ctx.addIssue({
            code: 'custom',
            path: ['capacity'],
            message: t('validation_too_small'),
          });
        }
      }

      if (values.end_date && values.end_minute && !isAfterStart(values)) {
        ctx.addIssue({
          code: 'custom',
          path: [
            values.end_date === values.start_date ? 'end_minute' : 'end_date',
          ],
          message: t('validation_invalid_date'),
        });
      }
    });

export const createBookingEventWaveSchema = (t: TFunction) =>
  z
    .object({
      name: z.string().min(1, { message: t('validation_required') }),
      description: z.string(),
      service_id: requiredSelect(t),
      capacity: z
        .string()
        .min(1, { message: t('validation_required') })
        .refine(isPositiveNumber, { message: t('validation_too_small') }),
      start_date: z.string().min(1, { message: t('validation_required') }),
      start_minute: requiredSelect(t),
      end_date: z.string().min(1, { message: t('validation_required') }),
      end_minute: requiredSelect(t),
      online_bookable: z.boolean(),
    })
    .superRefine((values, ctx) => {
      if (values.end_date && values.end_minute && !isAfterStart(values)) {
        ctx.addIssue({
          code: 'custom',
          // Within one day it is the time that is wrong, so the message belongs under the time.
          path: [
            values.end_date === values.start_date ? 'end_minute' : 'end_date',
          ],
          message: t('validation_invalid_date'),
        });
      }
    });

/** A place hold, so no coverage field: money for a registration is a separate reception sale. */
export const createBookingEventRegistrationSchema = (
  t: TFunction,
  requiredCustomFieldIds: number[] = [],
) =>
  z
    .object({
      client_id: z.string(),
      guest_info: z.string(),
      note: z.string(),
      staff_comment: z.string(),
      custom_field_values: customFieldValuesSchema,
    })
    .superRefine((values, ctx) => {
      refineClientOrGuest(t)(values, ctx);
      refineRequiredCustomFields(t, requiredCustomFieldIds)(values, ctx);
    });

export const createBookingEventRegistrationEditSchema = (
  t: TFunction,
  requiredCustomFieldIds: number[] = [],
) =>
  z
    .object({
      staff_comment: z.string(),
      custom_field_values: customFieldValuesSchema,
    })
    .superRefine(refineRequiredCustomFields(t, requiredCustomFieldIds));

export const createBookingEventRegistrationMoveSchema = (t: TFunction) =>
  z.object({
    event_wave_id: requiredSelect(t),
  });

export type BookingCompanyEventFormValues = z.infer<
  ReturnType<typeof createBookingCompanyEventSchema>
>;

export type BookingEventWaveFormValues = z.infer<
  ReturnType<typeof createBookingEventWaveSchema>
>;

export type BookingEventRegistrationFormValues = z.infer<
  ReturnType<typeof createBookingEventRegistrationSchema>
>;

export type BookingEventRegistrationEditFormValues = z.infer<
  ReturnType<typeof createBookingEventRegistrationEditSchema>
>;

export type BookingEventRegistrationMoveFormValues = z.infer<
  ReturnType<typeof createBookingEventRegistrationMoveSchema>
>;
